import { DatePickerPopover } from "$root/components/date-picker-popover";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { useTranslation } from "react-i18next";
import { parseMarketDateParam, marketDateBounds } from "./marketDate";

/**
 * Today is a real day in this window rather than an "unset", so returning to it sets the date like any
 * other pick — it goes through `setMarketDate`, which is what MDOT-25191's dirty-composition
 * confirmation hangs off. Disabled on today itself: greyed rather than hidden, so the footer keeps its
 * shape and the button says there is nowhere to go back to.
 */
function ReturnToTodayButton(props: {
	marketDate: string;
	today: string;
	setMarketDate(marketDate: string): void;
	close(): void;
}): React.JSX.Element {
	const { marketDate, today, setMarketDate, close } = props;
	const { t } = useTranslation();
	return (
		<Button
			size="x-small"
			palette="tertiary"
			disabled={marketDate === today}
			onClick={() => {
				setMarketDate(today);
				close();
			}}
			data-qualifier="CompositionEditor/MarketDate/Today"
		>
			{t("EDIT_COMPOSITION.REFERENCE_DATE.RETURN_TO_TODAY")}
		</Button>
	);
}

/** Built outside the component, so the footer's render prop is not a component declared during render. */
function returnToTodayAction(marketDate: string, today: string, setMarketDate: (marketDate: string) => void) {
	return (close: () => void) => (
		<ReturnToTodayButton marketDate={marketDate} today={today} setMarketDate={setMarketDate} close={close} />
	);
}

/**
 * The market date picker popover (MDOT-25190): the day the editor's holdings are priced at.
 *
 * Every day in range is selectable, weekends included: the min is the portfolio's last trade date
 * and the max is server-today. Today is both the default and the window's upper bound, and the
 * footer's "Return to today" sets it in one click — the same way back the as-of picker offers, so a
 * user who has moved the editor off today does not have to find today on a calendar to undo it.
 *
 * Picking or typing a day stages it; "Apply" is what sets the editor's date. Once MDOT-25191 lands,
 * changing the date on a dirty quantity composition raises a confirmation dialog, and a stray click
 * on a calendar cell should not be able to raise it.
 *
 * Everything else the popover does — the masked field, the draft it stages, the blur
 * canonicalisation, the range hint — is `DatePickerPopover`'s, shared with the portfolio history
 * view's as-of picker. What this file supplies is the window, the rule that settles the field, the
 * strings, and the two actions the footer carries.
 */
export function MarketDatePicker(props: {
	/** The date the editor is on (`YYYY-MM-DD`, local calendar); today when nothing was chosen. */
	marketDate: string;
	setMarketDate(marketDate: string): void;
	/** The portfolio's last trade date (a UTC timestamp or `YYYY-MM-DD`) — earliest selectable day. */
	lastTradeDate?: string;
	/** Rendered as the popover trigger (`asChild`), so it must be able to carry button props and a ref. */
	children: React.JSX.Element;
}): React.JSX.Element {
	const { marketDate, setMarketDate, lastTradeDate, children } = props;
	const { t } = useTranslation();
	const { min, max } = marketDateBounds({ lastTradeDate });
	// The window's upper bound is today, so the day "Return to today" goes to is the one `max` already
	// names — read from there rather than from a second call that could answer differently over midnight.
	const today = max;

	return (
		<DatePickerPopover
			value={marketDate}
			min={min}
			max={max}
			// The same rule that vets `?marketDate`, so what can be typed and what can be linked to are
			// one set rather than two that drift.
			accept={(day) => parseMarketDateParam(day, { lastTradeDate })}
			labels={{
				dialog: t("EDIT_COMPOSITION.REFERENCE_DATE.LABEL"),
				typeADate: t("EDIT_COMPOSITION.REFERENCE_DATE.TYPE_A_DATE"),
				commit: t("EDIT_COMPOSITION.REFERENCE_DATE.APPLY"),
			}}
			rangeHint={({ from, to }) =>
				from
					? t("EDIT_COMPOSITION.REFERENCE_DATE.RANGE_HINT", { from, to })
					: t("EDIT_COMPOSITION.REFERENCE_DATE.RANGE_HINT_NO_MIN", { to })
			}
			qualifier="CompositionEditor/MarketDate"
			onCommit={setMarketDate}
			secondaryAction={returnToTodayAction(marketDate, today, setMarketDate)}
		>
			{children}
		</DatePickerPopover>
	);
}
